import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import lodashCompact from 'lodash/compact';

import { SCREEN_ID_UTILITY_PARTNER_SETTINGS } from 'client/constants/screenIds';

import {
  hasRoleAdmin,
  hasRolePartnerAdmin,
  hasRolePartnerAccounting,
  UTILITY_USER_ROLE_ADMIN,
  UTILITY_USER_ROLE_PARTNER,
} from 'common/utility/userRoles';

import SrBox from 'client/components/SrBox';
import SrPartnerPageV2 from 'client/components/SrPartnerPageV2/SrPartnerPageV2';
import SrNavigationButton from 'client/components/SrNavigationButton/SrNavigationButton';

import UtilityPartnerActions from 'client/actions/UtilityPartner';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedUtilityRoles from 'client/helpers/allowedUtilityRoles';
import branch from 'client/core/branch';
import { isHomebuyerPartner } from 'common/utility/helpers';

@withTransitionHook
@allowedUtilityRoles({
  roles: [UTILITY_USER_ROLE_ADMIN, UTILITY_USER_ROLE_PARTNER],
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_UTILITY_PARTNER_SETTINGS)
@withScrollToTop
@branch(({ match }) => ({
  utilityPartner: ['utilityPartners', 'bySlug', match.params.partnerSlug],
  currentUser: ['currentUser'],
  utilityUser: ['utilityUser'],
}))
class UtilityPartnerSettingsPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Settings',
  });

  static fetchData(match, inject) {
    return UtilityPartnerActions.getBySlug(inject, {
      slug: match.params.partnerSlug,
    });
  }

  static propTypes = {
    utilityPartner: PropTypes.object,
    isTransitioning: PropTypes.bool,
  };

  render() {
    const { utilityUser, utilityPartner, isTransitioning } = this.props;

    const isAdmin = hasRoleAdmin(utilityUser.roles);

    const isPartnerAccounting =
      (hasRolePartnerAccounting(utilityUser.roles) ||
        hasRolePartnerAdmin(utilityUser.roles) ||
        isAdmin) &&
      utilityPartner.experienceConfiguration.detailedModeActive;

    if (isTransitioning && !utilityPartner) {
      return null;
    }

    if (!utilityPartner) {
      return <Redirect to="/" />;
    }

    const isHomebuyer = isHomebuyerPartner(utilityPartner);

    const navigationButtons = lodashCompact([
      {
        color: '#132630',
        iconUrl: '/images/tab-leads-white.svg',
        iconSize: 25,
        to: `/utility-profit/${utilityPartner.slug}/settings/team`,
        title: 'Team Members',
        subtitle: 'Manage your team members.',
      },
      isPartnerAccounting && {
        color: '#00A238',
        iconUrl: '/images/dollar-white.svg',
        to: `/utility-profit/${utilityPartner.slug}/settings/banks`,
        title: 'Payouts',
        subtitle: 'Setup collection method',
      },
      !isHomebuyer && {
        color: '#AD00AD',
        iconUrl: '/images/checkmark-icon-thick-white.svg',
        iconSize: 25,
        to: `/utility-profit/${utilityPartner.slug}/settings/tenant`,
        title: 'Tenant Settings',
        subtitle: 'Customize your tenant experience.',
      },
    ]);

    return (
      <SrPartnerPageV2
        backUrl={`/utility-profit/${utilityPartner.slug}`}
        navbarTitle={utilityPartner.name}
        navbarImage={utilityPartner.logoUrl}
        redirectToLastLocation={false}
        pageHeader="Account Settings"
      >
        {navigationButtons.map((button) => (
          <React.Fragment key={button.title}>
            <SrNavigationButton
              color={button.color}
              iconUrl={button.iconUrl}
              iconSize={button.iconSize}
              to={button.to}
              title={button.title}
              subtitle={button.subtitle}
            />
            <SrBox marginTop={10} />
          </React.Fragment>
        ))}
      </SrPartnerPageV2>
    );
  }
}

UtilityPartnerSettingsPage.displayName = 'UtilityPartnerSettingsPage';

export default UtilityPartnerSettingsPage;
